import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Trash2, Edit, X, Save, User, Calendar, ChevronLeft, ChevronRight, CheckCircle, Download, CheckSquare, Square } from "lucide-react";
import { format } from "date-fns";

const CustomerListPage = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // --- SENSOR LEVEL AKSES (BACA ROLE DARI LOCAL STORAGE) ---
  const currentUser = JSON.parse(localStorage.getItem('user') || "{}");
  const userRole = currentUser.role || "USER";
  const isReadOnly = userRole === "USER"; // True jika dia cuma Staff biasa

  // --- PAGINATION & FILTER ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const [filters, setFilters] = useState({ customer_id: "", company: "", rate_code: "", sales_ocs: "" });
  const [selectedIds, setSelectedIds] = useState([]); 

  // --- MODAL STATE ---
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [suggestedId, setSuggestedId] = useState({ last: "-", next: "" });
  
  const [newData, setNewData] = useState({
    customer_id: "", company: "", rate_code: "", rate_code_imp: "", rate_code_dom: "", sales_ocs: "",
    address1: "", address2: "", city: "", post_code: "", building: "",
    phone_area: "", phone1: "", phone2: "", contact_person: "", email_address: "",
    npwp: "", world_acc: "", date_approval: ""
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

  // --- FUNGSI PENCARI ID DINAMIS ---
  const fetchSuggestedId = async (prefix) => {
      if (!prefix) return;
      try {
          const res = await axios.get(`${API_URL}/customers/suggest-id?prefix=${prefix.toUpperCase()}`);
          setSuggestedId({ last: res.data.last_id, next: res.data.next_id });
      } catch (error) { 
          console.error("Gagal ambil saran ID", error); 
      }
  };

  // --- PEMANTAU KETIKAN CUSTOMER ID ---
  useEffect(() => {
      const prefixMatch = newData.customer_id.match(/^[A-Za-z]+/);
      if (prefixMatch && prefixMatch[0].length >= 2) {
          const timer = setTimeout(() => {
              fetchSuggestedId(prefixMatch[0]);
          }, 500);
          return () => clearTimeout(timer);
      } else {
          setSuggestedId({ last: "-", next: "" });
      }
  }, [newData.customer_id]);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/customers?limit=5000`);
      setCustomers(res.data.data || []);
    } catch (error) { toast.error("Gagal mengambil data customer"); } 
    finally { setIsLoading(false); }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    return dateString.replace("T", " ").split(" ")[0]; 
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); 
  };

  const filteredCustomers = customers.filter(item => {
    const matchID = filters.customer_id === "" || item.customer_id.toLowerCase().includes(filters.customer_id.toLowerCase());
    const matchCompany = filters.company === "" || item.company.toLowerCase().includes(filters.company.toLowerCase());
    
    const searchRate = filters.rate_code.toLowerCase();
    const matchRate = filters.rate_code === "" || (
        (item.rate_code || "").toLowerCase().includes(searchRate) ||
        (item.rate_code_imp || "").toLowerCase().includes(searchRate) ||
        (item.rate_code_dom || "").toLowerCase().includes(searchRate)
    );

    const matchSales = filters.sales_ocs === "" || (item.sales_ocs || "").toLowerCase().includes(filters.sales_ocs.toLowerCase());
    return matchID && matchCompany && matchRate && matchSales;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // --- LOGIKA EXCEL DOWNLOAD ---
  const handleSelect = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === currentCustomers.length) setSelectedIds([]);
    else setSelectedIds(currentCustomers.map(c => c.id));
  };

  const handleDownloadExcel = async (type) => {
    const idsToDownload = type === 'SELECTED' ? selectedIds : filteredCustomers.map(c => c.id);
    if (idsToDownload.length === 0) return toast.error("Tidak ada data untuk didownload");
    
    const loadingToast = toast.loading("Membuat file Excel...");
    try {
      const res = await axios.post(`${API_URL}/customers/export`, { ids: idsToDownload }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Database_Customer_${format(new Date(), 'ddMMyyyy')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel berhasil didownload!", { id: loadingToast });
    } catch (error) { toast.error("Gagal mendownload Excel", { id: loadingToast }); }
  };

  // --- CRUD ACTIONS ---
  const handleDelete = async (id, name) => {
    if (isReadOnly) return toast.error("Akses Ditolak! Anda tidak memiliki izin untuk menghapus.");
    if (!window.confirm(`Hapus customer ${name}?`)) return;
    try {
      await axios.delete(`${API_URL}/customers/${id}`);
      toast.success("Berhasil dihapus");
      fetchCustomers();
    } catch (error) { toast.error("Gagal menghapus"); }
  };

  const handleSaveEdit = async () => {
    if (isReadOnly) return;
    try {
        await axios.put(`${API_URL}/customers/${editData.id}`, editData);
        toast.success("Data berhasil diupdate!");
        setEditData(null);
        fetchCustomers();
    } catch (error) { toast.error("Gagal update data."); }
  };

  const handleSaveNew = async () => {
    if (isReadOnly) return;
    if (!newData.customer_id || !newData.company) return toast.error("Customer ID dan Nama Perusahaan wajib diisi!");
    try {
        await axios.post(`${API_URL}/customers`, newData);
        toast.success("Customer baru berhasil ditambahkan!");
        setIsAddOpen(false);
        setNewData({ 
            customer_id: "", company: "", rate_code: "", rate_code_imp: "", rate_code_dom: "", sales_ocs: "",
            address1: "", address2: "", city: "", post_code: "", building: "",
            phone_area: "", phone1: "", phone2: "", contact_person: "", email_address: "",
            npwp: "", world_acc: "", date_approval: ""
        });
        fetchCustomers();
    } catch (error) { toast.error("Gagal menambah customer."); }
  };

  const openEditModal = (cust) => {
      const cleanDate = formatDateOnly(cust.date_approval);
      setEditData({ ...cust, date_approval: cleanDate });
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      
      {/* HEADER & EXCEL BUTTONS */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-6 rounded-xl border shadow-sm">
        <div>
            <h1 className="text-2xl font-bold text-gray-900">Customers List</h1>
            <p className="text-gray-500">Total Database: {customers.length} Customers</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
            {selectedIds.length > 0 ? (
                <button onClick={() => handleDownloadExcel('SELECTED')} className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 transition shadow-md">
                    <Download size={18}/> Download {selectedIds.length} Pilihan
                </button>
            ) : (
                <button onClick={() => handleDownloadExcel('ALL')} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 transition shadow-md">
                    <Download size={18}/> Download Semua Filtered
                </button>
            )}
            
            {!isReadOnly && (
                <button onClick={() => setIsAddOpen(true)} className="bg-gray-900 hover:bg-black text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 transition shadow-md">
                    <Plus size={18} /> Add Customer
                </button>
            )}
        </div>
      </div>

      {/* FILTER SEARCH */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white p-4 rounded-xl border shadow-sm">
        <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input name="customer_id" value={filters.customer_id} onChange={handleFilterChange} placeholder="Cari ID..." className="pl-9 w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"/>
        </div>
        <input name="company" value={filters.company} onChange={handleFilterChange} placeholder="Cari Perusahaan..." className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"/>
        <input name="rate_code" value={filters.rate_code} onChange={handleFilterChange} placeholder="Cari Rate Code..." className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"/>
        <input name="sales_ocs" value={filters.sales_ocs} onChange={handleFilterChange} placeholder="Cari Sales OCS..." className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"/>
      </div>

      {/* TABEL DATA */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-gray-50 border-b font-bold text-gray-700">
                    <tr>
                        <th className="px-4 py-4 w-12 text-center">
                            <button onClick={handleSelectAll} className="text-gray-400 hover:text-blue-600">
                            {selectedIds.length === currentCustomers.length && currentCustomers.length > 0 ? <CheckSquare className="text-blue-600" /> : <Square />}
                            </button>
                        </th>
                        <th className="px-4 py-4">Customer ID</th>
                        <th className="px-4 py-4">Company</th>
                        <th className="px-4 py-4">Rate Codes (EXP, IMP, DOM)</th>
                        <th className="px-4 py-4">Sales OCS</th>
                        {!isReadOnly && <th className="px-4 py-4 text-center">Aksi</th>}
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {isLoading ? (
                        <tr><td colSpan={isReadOnly ? "5" : "6"} className="p-8 text-center text-gray-500">Loading data...</td></tr>
                    ) : filteredCustomers.length === 0 ? (
                        <tr><td colSpan={isReadOnly ? "5" : "6"} className="p-8 text-center text-gray-500">Tidak ada data ditemukan.</td></tr>
                    ) : (
                        currentCustomers.map((cust) => (
                            <tr key={cust.id} className={`hover:bg-gray-50 transition ${selectedIds.includes(cust.id) ? 'bg-blue-50' : ''}`}>
                                <td className="px-4 py-3 text-center">
                                    <button onClick={() => handleSelect(cust.id)} className="text-gray-300 hover:text-blue-600 transition">
                                    {selectedIds.includes(cust.id) ? <CheckSquare className="text-blue-600" /> : <Square />}
                                    </button>
                                </td>
                                <td onClick={() => setViewData(cust)} className="px-4 py-3 font-mono font-bold text-blue-600 cursor-pointer hover:underline">
                                    {cust.customer_id}
                                </td>
                                <td className="px-4 py-3 font-bold text-gray-900 whitespace-normal min-w-[200px]">{cust.company}</td>
                                <td className="px-4 py-3">
                                    <div className="flex flex-col gap-1 font-mono text-[10px] font-bold">
                                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 inline-block w-fit">EXP: {cust.rate_code || '-'}</span>
                                        <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded border border-orange-200 inline-block w-fit">IMP: {cust.rate_code_imp || '-'}</span>
                                        <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded border border-purple-200 inline-block w-fit">DOM: {cust.rate_code_dom || '-'}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-gray-500">{cust.sales_ocs || "-"}</td>
                                
                                {!isReadOnly && (
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <button onClick={() => openEditModal(cust)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"><Edit size={18} /></button>
                                            <button onClick={() => handleDelete(cust.id, cust.company)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 size={18} /></button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>

        {/* FOOTER PAGINATION */}
        {!isLoading && filteredCustomers.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
            <span className="text-sm text-gray-500">
                Menampilkan <strong>{indexOfFirstItem + 1}</strong> - <strong>{Math.min(indexOfLastItem, filteredCustomers.length)}</strong> dari <strong>{filteredCustomers.length}</strong> data
            </span>
            <div className="flex items-center gap-2">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="p-2 border rounded bg-white hover:bg-gray-100 disabled:opacity-50"><ChevronLeft size={16} /></button>
                <span className="text-sm font-medium px-2">Page {currentPage} of {totalPages}</span>
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 border rounded bg-white hover:bg-gray-100 disabled:opacity-50"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* --- MODAL VIEW DETAIL --- */}
      {viewData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="bg-blue-600 p-4 flex justify-between items-center text-white sticky top-0 z-10">
                    <h3 className="font-bold text-lg flex items-center gap-2"><User size={20}/> Detail Customer</h3>
                    <button onClick={() => setViewData(null)} className="hover:bg-blue-700 p-1 rounded"><X size={20}/></button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                    {/* INFO UTAMA */}
                    <div className="col-span-2 bg-blue-50 p-4 rounded-xl border border-blue-100">
                        <h4 className="font-bold text-blue-800 mb-2">Informasi Perusahaan</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="text-xs text-gray-500 font-bold">ID</label><p className="font-bold text-blue-600">{viewData.customer_id}</p></div>
                            <div><label className="text-xs text-gray-500 font-bold">NPWP</label><p>{viewData.npwp || "-"}</p></div>
                            <div className="col-span-2"><label className="text-xs text-gray-500 font-bold">Company</label><p className="font-bold text-lg">{viewData.company}</p></div>
                            <div>
                                <label className="text-xs text-gray-500 font-bold">Date Approval</label>
                                <p className="font-medium text-gray-900 flex items-center gap-1">
                                    <Calendar size={14} className="text-blue-600"/> 
                                    {formatDateOnly(viewData.date_approval) || "-"}
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* ALAMAT */}
                    <div className="bg-gray-50 p-4 rounded-xl border">
                        <h4 className="font-bold text-gray-700 mb-2">Alamat</h4>
                        <p className="font-medium">{viewData.building}</p>
                        <p>{viewData.address1}</p>
                        <p>{viewData.address2}</p>
                        <p>{viewData.city} {viewData.post_code}</p>
                    </div>
                    {/* KONTAK */}
                    <div className="bg-gray-50 p-4 rounded-xl border">
                        <h4 className="font-bold text-gray-700 mb-2">Kontak</h4>
                        <p><strong>CP:</strong> {viewData.contact_person}</p>
                        <p><strong>Email:</strong> {viewData.email_address}</p>
                        <p><strong>Phone 1:</strong> {viewData.phone1}</p>
                        <p><strong>Phone 2:</strong> {viewData.phone2}</p>
                    </div>
                    {/* SALES & AKUN */}
                    <div className="col-span-2 bg-orange-50 p-4 rounded-xl border border-orange-100 grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div><label className="text-xs text-orange-700 font-bold">Rate Export</label><p className="font-bold text-orange-900">{viewData.rate_code || "-"}</p></div>
                        <div><label className="text-xs text-orange-700 font-bold">Rate Import</label><p className="font-bold text-orange-900">{viewData.rate_code_imp || "-"}</p></div>
                        <div><label className="text-xs text-orange-700 font-bold">Rate Dom</label><p className="font-bold text-orange-900">{viewData.rate_code_dom || "-"}</p></div>
                        <div><label className="text-xs text-gray-500 font-bold">Sales OCS</label><p className="font-bold">{viewData.sales_ocs || "-"}</p></div>
                        <div><label className="text-xs text-gray-500 font-bold">World Acc</label><p className="font-bold">{viewData.world_acc || "-"}</p></div>
                    </div>
                </div>
                <div className="bg-gray-50 p-4 flex justify-end sticky bottom-0">
                    <button onClick={() => setViewData(null)} className="bg-gray-800 hover:bg-black text-white px-6 py-2 rounded-lg font-bold">Tutup</button>
                </div>
            </div>
        </div>
      )}

      {/* --- MODAL EDIT --- */}
      {editData && !isReadOnly && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="bg-orange-600 p-4 flex justify-between items-center text-white shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2"><Edit size={20}/> Edit Customer Lengkap</h3>
                    <button onClick={() => setEditData(null)} className="hover:bg-orange-700 p-1 rounded"><X size={20}/></button>
                </div>
                
                <div className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* INFO UTAMA */}
                    <div className="col-span-2 space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Informasi Utama</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Customer ID</label>
                                <input disabled value={editData.customer_id} className="w-full p-2 border rounded bg-gray-100 text-gray-500 cursor-not-allowed" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">NPWP</label>
                                <input value={editData.npwp || ""} onChange={(e)=>setEditData({...editData, npwp: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Nama Perusahaan (Company)</label>
                                <input value={editData.company || ""} onChange={(e)=>setEditData({...editData, company: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" />
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-gray-700">Date Approval</label>
                                <input 
                                    type="date" 
                                    value={editData.date_approval || ""} 
                                    onChange={(e)=>setEditData({...editData, date_approval: e.target.value})} 
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" 
                                />
                            </div>
                        </div>
                    </div>
                    {/* ALAMAT */}
                    <div className="space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Alamat Lengkap</h4>
                        <div><label className="block text-sm font-medium text-gray-700">Building Name</label><input value={editData.building || ""} onChange={(e)=>setEditData({...editData, building: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Address 1</label><input value={editData.address1 || ""} onChange={(e)=>setEditData({...editData, address1: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Address 2</label><input value={editData.address2 || ""} onChange={(e)=>setEditData({...editData, address2: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        <div className="grid grid-cols-2 gap-2">
                             <div><label className="block text-sm font-medium text-gray-700">City</label><input value={editData.city || ""} onChange={(e)=>setEditData({...editData, city: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">Post Code</label><input value={editData.post_code || ""} onChange={(e)=>setEditData({...editData, post_code: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        </div>
                    </div>
                    {/* KONTAK */}
                    <div className="space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Kontak</h4>
                        <div><label className="block text-sm font-medium text-gray-700">Contact Person</label><input value={editData.contact_person || ""} onChange={(e)=>setEditData({...editData, contact_person: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Email Address</label><input value={editData.email_address || ""} onChange={(e)=>setEditData({...editData, email_address: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        <div className="grid grid-cols-2 gap-2">
                             <div><label className="block text-sm font-medium text-gray-700">Phone 1</label><input value={editData.phone1 || ""} onChange={(e)=>setEditData({...editData, phone1: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">Phone 2</label><input value={editData.phone2 || ""} onChange={(e)=>setEditData({...editData, phone2: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        </div>
                         <div><label className="block text-sm font-medium text-gray-700">Phone Area</label><input value={editData.phone_area || ""} onChange={(e)=>setEditData({...editData, phone_area: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                    </div>
                    {/* SALES & AKUN */}
                    <div className="col-span-2 space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Akun & Sales</h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                             <div><label className="block text-sm font-medium text-orange-700">Rate Export</label><input value={editData.rate_code || ""} onChange={(e)=>setEditData({...editData, rate_code: e.target.value})} className="w-full p-2 border rounded bg-orange-50 focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-orange-700">Rate Import</label><input value={editData.rate_code_imp || ""} onChange={(e)=>setEditData({...editData, rate_code_imp: e.target.value})} className="w-full p-2 border rounded bg-orange-50 focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-orange-700">Rate Dom</label><input value={editData.rate_code_dom || ""} onChange={(e)=>setEditData({...editData, rate_code_dom: e.target.value})} className="w-full p-2 border rounded bg-orange-50 focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                             
                             <div><label className="block text-sm font-medium text-gray-700">Sales OCS</label><input value={editData.sales_ocs || ""} onChange={(e)=>setEditData({...editData, sales_ocs: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">World Acc</label><input value={editData.world_acc || ""} onChange={(e)=>setEditData({...editData, world_acc: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none" /></div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 p-4 flex justify-end gap-2 shrink-0">
                    <button onClick={() => setEditData(null)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium">Batal</button>
                    <button onClick={handleSaveEdit} className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 shadow-lg shadow-orange-200">
                        <Save size={18}/> Simpan Perubahan
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* --- MODAL ADD NEW (INI YANG BENAR!) --- */}
      {isAddOpen && !isReadOnly && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="bg-blue-600 p-4 flex justify-between items-center text-white shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2"><Plus size={20}/> Tambah Customer Baru</h3>
                    <button onClick={() => setIsAddOpen(false)} className="hover:bg-blue-700 p-1 rounded"><X size={20}/></button>
                </div>
                
                <div className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* INFO UTAMA */}
                    <div className="col-span-2 space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Informasi Utama</h4>
                        <div className="grid grid-cols-2 gap-4">
                            {/* DISINI KITA PASANG KOTAK CUSTOMER ID YANG PINTAR ITU 👇 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Customer ID <span className="text-red-500">*</span></label>
                                <input 
                                    value={newData.customer_id} 
                                    onChange={(e)=>setNewData({...newData, customer_id: e.target.value.toUpperCase()})} 
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none uppercase font-bold text-blue-700" 
                                    placeholder="Ketik awalan (Misal: JR, CD...)"
                                />
                                {newData.customer_id.length >= 2 && (
                                    <p className="text-[11px] text-gray-500 mt-1.5 font-bold bg-red-50 p-1.5 rounded border border-red-100 inline-block">
                                        Terakhir dipakai: <span className="text-red-600">{suggestedId.last || "Mencari..."}</span>
                                    </p>
                                )}
                            </div>
                            
                            <div><label className="block text-sm font-medium text-gray-700">NPWP</label><input value={newData.npwp} onChange={(e)=>setNewData({...newData, npwp: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                            <div className="col-span-2"><label className="block text-sm font-medium text-gray-700">Nama Perusahaan (Company) <span className="text-red-500">*</span></label><input value={newData.company} onChange={(e)=>setNewData({...newData, company: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Date Approval</label>
                                <input type="date" value={newData.date_approval} onChange={(e)=>setNewData({...newData, date_approval: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" />
                            </div>
                        </div>
                    </div>
                    {/* ALAMAT */}
                    <div className="space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Alamat Lengkap</h4>
                        <div><label className="block text-sm font-medium text-gray-700">Building Name</label><input value={newData.building} onChange={(e)=>setNewData({...newData, building: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Address 1</label><input value={newData.address1} onChange={(e)=>setNewData({...newData, address1: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Address 2</label><input value={newData.address2} onChange={(e)=>setNewData({...newData, address2: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        <div className="grid grid-cols-2 gap-2">
                             <div><label className="block text-sm font-medium text-gray-700">City</label><input value={newData.city} onChange={(e)=>setNewData({...newData, city: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">Post Code</label><input value={newData.post_code} onChange={(e)=>setNewData({...newData, post_code: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        </div>
                    </div>
                    {/* KONTAK */}
                    <div className="space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Kontak</h4>
                        <div><label className="block text-sm font-medium text-gray-700">Contact Person</label><input value={newData.contact_person} onChange={(e)=>setNewData({...newData, contact_person: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Email Address</label><input value={newData.email_address} onChange={(e)=>setNewData({...newData, email_address: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        <div className="grid grid-cols-2 gap-2">
                             <div><label className="block text-sm font-medium text-gray-700">Phone 1</label><input value={newData.phone1} onChange={(e)=>setNewData({...newData, phone1: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">Phone 2</label><input value={newData.phone2} onChange={(e)=>setNewData({...newData, phone2: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        </div>
                         <div><label className="block text-sm font-medium text-gray-700">Phone Area</label><input value={newData.phone_area} onChange={(e)=>setNewData({...newData, phone_area: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                    </div>
                    {/* SALES & AKUN */}
                    <div className="col-span-2 space-y-4">
                        <h4 className="font-bold text-gray-700 border-b pb-2">Akun & Sales</h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                             <div><label className="block text-sm font-medium text-orange-700">Rate Export</label><input value={newData.rate_code} onChange={(e)=>setNewData({...newData, rate_code: e.target.value})} className="w-full p-2 border rounded bg-orange-50 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-orange-700">Rate Import</label><input value={newData.rate_code_imp} onChange={(e)=>setNewData({...newData, rate_code_imp: e.target.value})} className="w-full p-2 border rounded bg-orange-50 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-orange-700">Rate Dom</label><input value={newData.rate_code_dom} onChange={(e)=>setNewData({...newData, rate_code_dom: e.target.value})} className="w-full p-2 border rounded bg-orange-50 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                             
                             <div><label className="block text-sm font-medium text-gray-700">Sales OCS</label><input value={newData.sales_ocs} onChange={(e)=>setNewData({...newData, sales_ocs: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">World Acc</label><input value={newData.world_acc} onChange={(e)=>setNewData({...newData, world_acc: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" /></div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 p-4 flex justify-end gap-2 shrink-0">
                    <button onClick={() => setIsAddOpen(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium">Batal</button>
                    <button onClick={handleSaveNew} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 shadow-lg shadow-blue-200">
                        <CheckCircle size={18}/> Simpan
                    </button>
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default CustomerListPage;